import type { Level } from "../api/types";
import type { Awaiting } from "../components/awaiting";
import type { NumberedStep } from "../components/StepMark";
import { isRotationCode, rotationError } from "../centacRotation";
import { k, t } from "../i18n/i18n";

/** Parcours balisé des Données initiales (demande d'Alexis du 31/10/2026 : « qu'il sache quoi remplir et à quel
 *  moment ») : les rubriques deviennent des étapes numérotées, chacune avec son état, l'étape attendue (`next`), les
 *  préalables manquants du bouton principal (`missing`) et l'avancement (`progress`). Logique pure, sans React : la
 *  vue lui donne ce qu'elle sait déjà, elle rend les étapes.
 *
 *  Étapes (refonte du parcours du 02/11/2026, « un grand fouillis, vraiment pas clair ») : 1 Dossier · 2 Force amie ·
 *  3 Lieu · 4 Date et météo · 5 Ennemi, toujours cinq, sans trou ; « Date » seule en CENTAC (aucun réglage météo).
 *  1 à 3 requises ; 4 et 5 réglées par défaut. Drones, Environnement, robotisation et profil du chef ENI ne sont plus
 *  des étapes : ils vivent dans « Options (facultatif) », après les cinq étapes (`optionsSummary`).
 *
 *  Parcours guidé (accordéon, une seule étape ouverte) : `firstOpenStep`, `stepAfter`, `primaryMode`, `railState`.
 *
 *  États : `done` (coche), `todo` (à remplir), `review` (valeur à revoir), `waiting` (en attente, avec sa raison),
 *  `optional` (facultative), `pending` (liste en chargement : numéro seul, sans « À remplir » qui clignoterait).
 *  `blocking` : préalable du bouton principal — l'appui le désigne au lieu d'enregistrer (R12).
 *
 *  Construction de l'entrée dans `MissionSummary` (intégration) — toutes ces valeurs y sont déjà calculées :
 *  - `level` : `level` (`draft.parameters.level ?? "REGIMENT"`) ; `saved` : `Boolean(current)` ;
 *    `readOnly` : `lockedDossier` ; `writes` : `kind !== "place"` (« Placer le terrain » n'écrit rien : ni le nom ni
 *    une valeur introuvable ne le bloquent, comme `blocker` et `submit` aujourd'hui) ;
 *  - `named` : `Boolean(draft.name.trim())` ; `rotation` : `draft.parameters.centac_rotation` ;
 *  - `mission` : `draft.parameters.mission` ; `unknownForce`, `unknownMission` : les constantes du même nom ;
 *  - `lieu`, dans cet ordre : `layout === "centac"` → `{ kind: "centac" }` ; `newTerrain` → `{ kind: "new", named }`
 *    (lieu du nouveau terrain nommé, `parameters.place`) ; `missingTerrain` → `{ kind: "terrain", status: "missing" }` ;
 *    `mapKind === "user"` → `{ kind: "terrain", status }` d'après `chosenZone.status` (« ready » → `ready`, « failed »
 *    ou « cancelled » → `interrupted`, sinon `preparing`) ; sinon `{ kind: "camp", chosen }`, `chosen` faux pour la carte
 *    « Camp de manœuvre » sans camp (`layout === "cards" && site === "camp" && mapKind !== "camp"`) ou la liste
 *    unique vide (`layout === "list" && !draft.mapSlug`), la règle « camp » de `blocker` ;
 *  - `weatherGap` : écart de la météo du brouillon sur le climat du lieu (`weatherGap(options, key)` non nul, que
 *    `WeatherField` fera remonter) ;
 *  - `eniLegacy` : la constante du même nom ; `customEnemyMissing` : `Boolean(chosenCustom) && !chosenCustomSummary
 *    && customListing.isSuccess` ;
 *  - `loading.forces` : `forcesQuery.isPending || (missionsQuery.isFetching && !missionsQuery.data)` ;
 *    `loading.lieu` : `!listsLoaded || !offer.known` ; `loading.enemies` : `!isCentac && (!eniListing ||
 *    customListing.isPending)`.
 *  Puis (parcours guidé, `MissionFolds.tsx`) : rail `StepsRail` (`railState`, `railLabel`), un volet `FoldSection` par
 *  étape (pastille `stepMarkState`, badge `stepBadge`, résumé `summaryLine`), pied du panneau (« Continuer » selon
 *  `primaryMode`, `nextLine(next)` sur un dossier enregistré), bouton `blocked={missing.length > 0}` avec `title=
 *  {missingLine(missing)}` et `onBlocked` → désignation de `missing[0].target` ; `submit` désigne de même `missing[0]`
 *  (le nom, le camp et la rotation gardent leurs désignations : `designateName`, `designateChoice`,
 *  `designateRotation`). */

export type StepKey = "dossier" | "force" | "lieu" | "date" | "ennemi";
/** Volet ouvert de l'accordéon : une étape, ou les options facultatives. */
export type Fold = StepKey | "options";
export type StepState = "done" | "todo" | "review" | "waiting" | "optional" | "pending";

/** Champ désigné par une étape (cibles `data-attention` des Données initiales ; `dossier-place` : champ « Lieu du
 *  nouveau terrain », `dossier-enemy` : champ « Ennemi », à poser par l'intégration). */
export type StepField =
  | "dossier-name"
  | "dossier-centac-rotation"
  | "dossier-odb"
  | "dossier-mission"
  | "dossier-camp"
  | "dossier-place"
  | "dossier-weather"
  | "dossier-enemy"
  | "dossier-eni-odb"
  | "dossier-drones";

/** Ce que désigne l'étape : un champ, le bouton principal (« Placer le terrain »), l'avancement du terrain dans la vue
 *  Manœuvre (`retry` : « Réessayer » d'une préparation interrompue), ou la rubrique elle-même. */
export type StepTarget =
  | { kind: "field"; field: StepField }
  | { kind: "save" }
  | { kind: "zone"; retry: boolean }
  | { kind: "section" };

export interface Step {
  key: StepKey;
  /** Numéro affiché (1 à 5). */
  n: number;
  /** Titre de la rubrique (clé à passer à `t`). */
  title: string;
  state: StepState;
  /** Préalable du bouton principal (états `todo` et `review`, ou `pending` le temps d'une liste). */
  blocking: boolean;
  /** Raison courte d'une attente ou d'un rappel (clé à passer à `t`). */
  reason?: string;
  /** Ce qu'il faut faire, en minuscules après « — » (clé à passer à `t`). */
  action?: string;
  target: StepTarget;
}

/** Lieu de l'exercice tel que la rubrique « Lieu » le connaît. */
export type StepsPlace =
  | { kind: "centac" }
  | { kind: "camp"; chosen: boolean }
  | { kind: "terrain"; status: "ready" | "preparing" | "interrupted" | "missing" }
  | { kind: "new"; named: boolean };

export interface StepsInput {
  level: Level;
  saved: boolean;
  readOnly: boolean;
  /** Le bouton principal écrit le dossier (faux pour « Placer le terrain »). */
  writes: boolean;
  /** Nom du dossier saisi (ignoré en CENTAC : la rotation le nomme). */
  named: boolean;
  /** Numéro de la rotation CENTAC saisi (« NN/AA »). */
  rotation?: string | null;
  mission: string | null | undefined;
  unknownForce: boolean;
  unknownMission: boolean;
  lieu: StepsPlace;
  weatherGap: boolean;
  eniLegacy: boolean;
  customEnemyMissing: boolean;
  loading: { forces: boolean; lieu: boolean; enemies: boolean };
}

export interface MissionSteps {
  steps: Step[];
  next: Step | null;
  missing: Step[];
  progress: number;
}

const TITLES: Record<StepKey, string> = {
  dossier: k("Dossier"),
  force: k("Force amie"),
  lieu: k("Lieu de l'exercice"),
  date: k("Date et météo"),
  ennemi: k("Ennemi"),
};
/** Libellés courts des repères du rail de progression (en haut du panneau). */
const RAIL_LABELS: Record<StepKey, string> = {
  dossier: k("Dossier"),
  force: k("Force"),
  lieu: k("Lieu"),
  date: k("Date"),
  ennemi: k("Ennemi"),
};
const CENTAC_DATE = k("Date");

const field = (name: StepField): StepTarget => ({ kind: "field", field: name });
const SECTION: StepTarget = { kind: "section" };

type Draft = Omit<Step, "n" | "title" | "blocking"> & { blocking?: boolean };

function dossierStep(o: StepsInput): Draft {
  if (o.level === "CENTAC") {
    const target = field("dossier-centac-rotation");
    const action = k("saisir le numéro de la rotation");
    // Numéro mal formé, ou absent à la création : il nomme le dossier (registres A119, A122).
    if (rotationError(o.rotation) !== null || (!o.saved && !isRotationCode(o.rotation)))
      return { key: "dossier", state: "todo", blocking: true, action, target };
    // Dossier enregistré sans numéro : demandé avant la rédaction de l'OPO N°1, il ne bloque pas l'enregistrement.
    if (!isRotationCode(o.rotation))
      return { key: "dossier", state: "todo", reason: k("Numéro demandé avant la rédaction de l'OPO N°1"), action, target };
    return { key: "dossier", state: "done", target };
  }
  const target = field("dossier-name");
  if (!o.named) return { key: "dossier", state: "todo", blocking: o.writes, action: k("nommer le dossier"), target };
  return { key: "dossier", state: "done", target };
}

function forceStep(o: StepsInput): Draft {
  if (o.unknownForce)
    return {
      key: "force",
      state: "review",
      blocking: o.writes,
      // Compagnie : la force commandée est un S/GTIA, choisi dans un GTIA.
      action: o.level === "COMPAGNIE" ? k("choisir le S/GTIA commandé") : k("choisir l'ordre de bataille"),
      target: field("dossier-odb"),
    };
  if (o.unknownMission)
    return { key: "force", state: "review", blocking: o.writes, action: k("choisir la mission"), target: field("dossier-mission") };
  // Nouveau terrain : l'étape Terrain de la vue Manœuvre refuserait le tracé sans mission.
  if (o.lieu.kind === "new" && !o.mission)
    return { key: "force", state: "todo", blocking: true, action: k("choisir la mission"), target: field("dossier-mission") };
  return { key: "force", state: "done", target: field("dossier-odb") };
}

function lieuStep(o: StepsInput): Draft {
  const lieu = o.lieu;
  if (lieu.kind === "centac") return { key: "lieu", state: "done", target: field("dossier-camp") };
  if (lieu.kind === "camp")
    return lieu.chosen
      ? { key: "lieu", state: "done", target: field("dossier-camp") }
      : { key: "lieu", state: "todo", blocking: true, action: k("choisir le lieu de l'exercice"), target: field("dossier-camp") };
  if (lieu.kind === "new") {
    if (!lieu.named)
      return { key: "lieu", state: "todo", blocking: true, action: k("nommer le lieu du nouveau terrain"), target: field("dossier-place") };
    // Nouveau dossier : « Créer et placer le terrain » fait la suite ; dossier enregistré : le terrain reste à placer.
    if (!o.saved) return { key: "lieu", state: "done", target: field("dossier-place") };
    return {
      key: "lieu",
      state: "waiting",
      reason: k("Terrain à placer"),
      action: k("placer le terrain dans la vue Manœuvre"),
      target: { kind: "save" },
    };
  }
  switch (lieu.status) {
    case "missing":
      return { key: "lieu", state: "todo", blocking: true, action: k("choisir un autre terrain"), target: field("dossier-camp") };
    case "preparing":
      return { key: "lieu", state: "waiting", reason: k("En préparation"), target: { kind: "zone", retry: false } };
    case "interrupted":
      return { key: "lieu", state: "review", action: k("relancer la préparation du terrain"), target: { kind: "zone", retry: true } };
    default:
      return { key: "lieu", state: "done", target: field("dossier-camp") };
  }
}

function dateStep(o: StepsInput, lieu: Draft): Draft {
  // CENTAC : aucun réglage météo, la météo de saison se retouche dans l'ordre de la rotation.
  if (o.level === "CENTAC") return { key: "date", state: "done", target: SECTION };
  const target = field("dossier-weather");
  if (lieu.state === "pending") return { key: "date", state: "pending", target };
  if (lieu.state === "todo") return { key: "date", state: "waiting", reason: k("En attente du lieu"), target };
  if (o.weatherGap) return { key: "date", state: "review", action: k("revoir la météo"), target };
  return { key: "date", state: "done", target };
}

function ennemiStep(o: StepsInput): Draft {
  // CENTAC : ennemi imposé par la rotation (Titan, joué par la FORAD).
  if (o.level === "CENTAC") return { key: "ennemi", state: "done", target: SECTION };
  if (o.eniLegacy) return { key: "ennemi", state: "review", action: k("choisir l'ordre de bataille ennemi"), target: field("dossier-eni-odb") };
  if (o.customEnemyMissing) return { key: "ennemi", state: "review", action: k("choisir l'ennemi"), target: field("dossier-enemy") };
  return { key: "ennemi", state: "done", target: SECTION };
}

/** Liste en chargement : l'étape montre son numéro seul (ni coche ni « À remplir » qui changeraient à l'arrivée de la
 *  liste) ; un préalable reste un préalable — le bouton ne laisse pas enregistrer un camp pas encore connu. */
function whileLoading(step: Draft, loading: boolean): Draft {
  return loading ? { ...step, state: "pending", reason: undefined, action: undefined } : step;
}

/** Étapes des Données initiales, étape attendue, préalables manquants et avancement (voir l'en-tête). */
export function missionSteps(input: StepsInput): MissionSteps {
  const centac = input.level === "CENTAC";
  const lieu = whileLoading(lieuStep(input), input.loading.lieu && input.lieu.kind !== "centac");
  const drafts: Draft[] = [
    dossierStep(input),
    whileLoading(forceStep(input), input.loading.forces),
    lieu,
    dateStep(input, lieu),
    whileLoading(ennemiStep(input), input.loading.enemies && !centac),
  ];
  const steps: Step[] = drafts.map((d, i) => {
    const title = d.key === "date" && centac ? CENTAC_DATE : TITLES[d.key];
    const step: Step = { ...d, n: i + 1, title, blocking: Boolean(d.blocking) && d.state !== "done" };
    // Lecture seule : numéros et coches seulement — rien à remplir, rien n'est attendu.
    if (input.readOnly && step.state !== "done" && step.state !== "optional")
      return { key: step.key, n: step.n, title, state: "pending", blocking: false, target: step.target };
    return step;
  });

  const asked = (s: Step) => s.state === "todo" || s.state === "review";
  const next =
    steps.find((s) => asked(s) && s.blocking) ??
    steps.find((s) => asked(s) && !s.blocking) ??
    steps.find((s) => s.state === "waiting" && s.action !== undefined) ??
    null;
  const missing = steps.filter((s) => s.blocking);
  const progress = steps.filter((s) => s.state === "done" || s.state === "optional").length / steps.length;
  return { steps, next, missing, progress };
}

/** Champs en surbrillance tant qu'ils ne sont pas renseignés (demande d'Alexis du 01/11/2026, `components/awaiting.ts`) :
 *  ceux des préalables du bouton principal (`missing`) à remplir ou à revoir — nom du dossier, rotation CENTAC, lieu
 *  de l'exercice, lieu du nouveau terrain, mission manquante, ordre de bataille ou mission introuvables —, le champ de
 *  l'étape attendue (`next`) un peu plus marqué. Seulement les préalables : un rappel non bloquant (météo à revoir,
 *  ennemi à reprendre, numéro de rotation demandé plus tard) garde son badge sans allumer son champ, et l'écran ne se
 *  couvre pas de cadres bleus. Ni un préalable en chargement (`pending`), ni une cible autre qu'un champ (bouton
 *  principal, avancement du terrain) ; rien en lecture seule ni sur un dossier enregistré complet (`missing` vide). */
export function awaitedFields(result: Pick<MissionSteps, "missing" | "next">): Partial<Record<StepField, Exclude<Awaiting, "off">>> {
  const fields: Partial<Record<StepField, Exclude<Awaiting, "off">>> = {};
  for (const step of result.missing) {
    if (step.target.kind !== "field" || (step.state !== "todo" && step.state !== "review")) continue;
    fields[step.target.field] ??= result.next?.key === step.key ? "next" : "missing";
  }
  return fields;
}

// --- Textes ----------------------------------------------------------------------------------------------------

export type StepBadgeTone = "accent" | "neutral" | "warn" | "run";

/** Badge à droite du titre de la rubrique (aucun pour une étape faite ou en chargement) : « À remplir » (accent pour
 *  l'étape attendue), « À revoir », la raison d'une attente (« En préparation » au ton « en cours »), « Facultatif ». */
export function stepBadge(step: Step, next: Step | null): { text: string; tone: StepBadgeTone } | null {
  switch (step.state) {
    case "todo":
      return { text: t("À remplir"), tone: next?.key === step.key ? "accent" : "neutral" };
    case "review":
      return { text: t("À revoir"), tone: "warn" };
    case "waiting":
      return { text: t(step.reason ?? "En attente"), tone: step.reason === "En préparation" ? "run" : "neutral" };
    case "optional":
      return { text: t("Facultatif"), tone: "neutral" };
    default:
      return null;
  }
}

/** État de la pastille numérotée : l'étape attendue, à remplir ou à revoir, prend l'anneau accent. */
export function stepMarkState(step: Step, next: Step | null): NumberedStep {
  return next?.key === step.key && (step.state === "todo" || step.state === "review") ? "next" : step.state;
}

/** Phrase du pied du panneau : « Ensuite : 3. Lieu de l'exercice — choisir le lieu de l'exercice ». */
export function nextLine(step: Step): string {
  // `next` a toujours une action : une étape à remplir ou à revoir en a une, une attente n'est retenue qu'avec la sienne.
  return t("Ensuite : {n}. {etape} — {action}", { n: step.n, etape: t(step.title), action: t(step.action ?? "") });
}

/** Survol du bouton principal bloqué : « Il manque : Dossier, Lieu de l'exercice ». */
export function missingLine(missing: readonly Step[]): string {
  return t("Il manque : {liste}", { liste: missing.map((s) => t(s.title)).join(", ") });
}

/** Nom de la barre d'avancement pour les lecteurs d'écran. */
export function stepsProgressLabel(result: MissionSteps): string {
  const done = result.steps.filter((s) => s.state === "done" || s.state === "optional").length;
  return t("Étapes faites : {fait} sur {total}", { fait: done, total: result.steps.length });
}

// --- Parcours guidé ------------------------------------------------------------------------------------------------
// Accordéon des Données initiales : une seule étape ouverte à la fois. Nouveau dossier : les étapes se valident une à
// une par « Continuer » (`confirmed`), la dernière par « Créer le dossier ». Dossier enregistré : toutes ses étapes
// sont tenues pour validées, repliées en résumés, et le bouton principal reste « Enregistrer ».

/** Étape ouverte à l'arrivée : nouveau dossier, la première ; dossier enregistré, la première étape qui retient le
 *  bouton principal (préalable manquant), sinon aucune — tout est replié en résumés. */
export function firstOpenStep(result: Pick<MissionSteps, "steps" | "missing">, saved: boolean): StepKey | null {
  if (!saved) return result.steps[0]?.key ?? null;
  return result.missing[0]?.key ?? null;
}

/** Étape suivante à valider après `from` (dans l'ordre, puis en reprenant au début), `from` exclue ; null : toutes
 *  les étapes sont validées. */
export function stepAfter(steps: readonly Step[], confirmed: ReadonlySet<StepKey>, from: Fold | null): StepKey | null {
  const at = steps.findIndex((s) => s.key === from);
  const order = at < 0 ? steps : [...steps.slice(at + 1), ...steps.slice(0, at)];
  return order.find((s) => !confirmed.has(s.key))?.key ?? null;
}

/** Bouton principal : « Continuer » tant qu'une étape d'un nouveau dossier reste à valider après celle ouverte ;
 *  sinon le bouton d'enregistrement (« Créer le dossier », « Enregistrer »…). */
export function primaryMode(o: { saved: boolean; open: Fold | null; steps: readonly Step[]; confirmed: ReadonlySet<StepKey> }): "continue" | "save" {
  if (o.saved) return "save";
  return stepAfter(o.steps, o.confirmed, o.open) === null ? "save" : "continue";
}

/** Repère du rail : `current` (étape ouverte), `done` (validée et complète), `todo` (le reste). */
export type RailState = "current" | "done" | "todo";
export function railState(step: Step, o: { open: Fold | null; confirmed: ReadonlySet<StepKey>; saved: boolean }): RailState {
  if (o.open === step.key) return "current";
  return (o.saved || o.confirmed.has(step.key)) && step.state === "done" ? "done" : "todo";
}

/** Libellé court d'une étape sur le rail (« Lieu », « Date »). */
export function railLabel(step: Step): string {
  return t(RAIL_LABELS[step.key]);
}

/** Ligne de résumé d'une étape repliée : les morceaux connus, séparés par « · ». */
export function summaryLine(parts: readonly (string | null | undefined | false)[]): string {
  return parts.filter((part): part is string => Boolean(part && part.trim())).join(" · ");
}
